import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectOptAuthCodesState,
  selectOtpCodeExpirationSeconds,
} from '../../redux/data/selectors';
import Popup from '../common/Popup';
import Button from '../Button';
import TextInput from '../TextInput';
import ErrorBlock from '../common/ErrorBlock';
import styles from './index.styl';
import MfaDialog from '../MfaDialog';
import Loader from '../common/Loader';
import * as login from '../../redux/auth/login';
import { selectAuthLoginData } from '../../redux/auth/selectors';
import { PATTERNS } from '../../constants/validations';
import loginValidator from '../../utils/validations/loginValidator';
import { actions as otpAuthCodesActions } from '../../redux/data/optAuthCodes';
import { defaultValidator } from '../../utils/validations/defaultValidator';
import InvitationModal from './InvitationModal';
import {
  LOGIN_ERRORS,
  LOGIN_ERROR_CODES,
  LOGIN_INVITATION_ERRORS,
} from '../../constants/errors';

const INIT_INVITATION_MODAL_DATA = {
  showInvitationModal: false,
  loginValue: '',
};

const INIT_OTP_ERROR_DATA = {
  error: false,
  errorText: '',
};

const HTTP_STATUS_TOO_MANY_REQUESTS = 429;
const HTTP_STATUS_UNAUTHORIZED = 401;
const HTTP_STATUS_LOCKED = 403;

const OtpModal = ({
  open,
  formatMessage,
  onClose,
  setIsAnyOfOtpPathModalsOpen,
}) => {
  const dispatch = useDispatch();
  const [fieldValue, setFieldValue] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [invitationModalData, setInvitationModalData] = useState(
    INIT_INVITATION_MODAL_DATA,
  );
  const [isOtpConfirmationOpened, setIsOtpConfirmationOpened] = useState(false);
  const [otpErrorData, setOtpErrorData] = useState(INIT_OTP_ERROR_DATA);

  const { sending, succeed, error, rejected } = useSelector(
    selectOptAuthCodesState,
  );

  const { rejected: loginRejected, error: loginError } = useSelector(
    selectAuthLoginData,
  );

  const otpCodeExpirationSeconds = useSelector(selectOtpCodeExpirationSeconds);

  const isEmail = value =>
    defaultValidator(PATTERNS.HAS_ALPHABETICAL_CHARACTER, value);

  const isValidFieldValue = value => {
    const isValid = value ? loginValidator(value) : true;
    if (!isValid) {
      const loginErrorId = isEmail(value)
        ? 'errors.validation.invalidLoginEmail'
        : 'errors.validation.invalidLoginPhone';
      setFieldError(formatMessage({ id: loginErrorId }));
    }
    return isValid;
  };

  const resetLoginError = () => {
    if (fieldError) setFieldError('');
  };

  const handleCancelBtnClick = () => {
    onClose();
    setFieldError('');
    setOtpErrorData(INIT_OTP_ERROR_DATA);
  };

  const handleTextInputChange = value => {
    setFieldValue(value);
  };

  const handleSubmitPasscode = () => {
    dispatch(otpAuthCodesActions.send({ login: fieldValue }));
  };

  const handleCloseOtpDialog = () => {
    dispatch(login.actions.clear());
    dispatch(otpAuthCodesActions.clear());
    setIsOtpConfirmationOpened(false);
    setOtpErrorData(INIT_OTP_ERROR_DATA);
    setFieldValue('');
  };

  const handleResendOtpCode = () => {
    setOtpErrorData(INIT_OTP_ERROR_DATA);
    handleSubmitPasscode();
  };

  const handleCloseInvitationModal = () => {
    dispatch(otpAuthCodesActions.clear());
    setInvitationModalData(INIT_INVITATION_MODAL_DATA);
    setFieldValue('');
  };

  const handleOtpLogin = code => {
    dispatch(
      login.actions.send({
        login: fieldValue,
        otpCode: code,
      }),
    );
  };

  useEffect(() => {
    if (succeed) {
      setIsOtpConfirmationOpened(Boolean(succeed));
      handleCancelBtnClick();
    }
  }, [succeed]);

  useEffect(() => {
    if (rejected || loginRejected) {
      const code = get(error, 'code');
      const status = get(error, 'status');

      const loginStatus = get(loginError, 'status');
      const loginCode = get(loginError, 'code');

      if (loginStatus === HTTP_STATUS_UNAUTHORIZED) {
        setOtpErrorData({
          error: true,
          errorText: LOGIN_ERRORS[loginCode] || 'mfa.dialog.error.message',
        });
      } else if (status === HTTP_STATUS_TOO_MANY_REQUESTS) {
        setOtpErrorData({
          error: true,
          errorText: 'otp.dialog.error.tooManyRequests',
        });
      } else if (
        status === HTTP_STATUS_LOCKED ||
        loginStatus === HTTP_STATUS_LOCKED
      ) {
        setOtpErrorData({
          error: true,
          errorText: 'errors.login.authenticationLockout',
        });
      } else if (LOGIN_INVITATION_ERRORS.includes(code)) {
        setInvitationModalData({
          showInvitationModal: true,
          loginValue: fieldValue,
        });
        handleCancelBtnClick();
      } else if (
        code === LOGIN_ERROR_CODES.AUTHENTICATION_LOCKOUT ||
        loginCode === LOGIN_ERROR_CODES.AUTHENTICATION_LOCKOUT
      ) {
        setOtpErrorData({
          error: true,
          errorText: 'errors.login.authenticationLockout',
        });
      }
    } else {
      setOtpErrorData(INIT_OTP_ERROR_DATA);
    }
  }, [rejected, error, loginRejected, loginError]);

  useEffect(() => {
    if (loginRejected) {
      dispatch(otpAuthCodesActions.clear());
    }
  }, [loginRejected]);

  useEffect(() => {
    if (rejected) {
      dispatch(login.actions.clear());
    }
  }, [rejected]);

  useEffect(() => {
    setIsAnyOfOtpPathModalsOpen(
      isOtpConfirmationOpened || invitationModalData.showInvitationModal,
    );
  }, [isOtpConfirmationOpened, invitationModalData.showInvitationModal]);

  return (
    <div>
      <Popup opened={open} withHeading={false} mobileFullScreen={false}>
        <div className={styles.modalWrapper}>
          {sending && (
            <div className={styles.loader}>
              <Loader />
            </div>
          )}
          <h1 className={styles.title}>
            {formatMessage({ id: 'otp.dialog.title' })}
          </h1>

          <div className={styles.contentText}>
            {formatMessage({ id: 'otp.dialog.contentText' })}
          </div>

          <TextInput
            className={styles.textField}
            id="email-or-phone"
            label={formatMessage({ id: 'profile.fields.emailOrPhone' })}
            name="emailOrPhone"
            placeholder={formatMessage({ id: 'profile.fields.emailOrPhone' })}
            required
            rounded
            onBlur={isValidFieldValue}
            onFocus={resetLoginError}
            onChange={handleTextInputChange}
          />

          <If condition={Boolean(fieldError || otpErrorData.error)}>
            <ErrorBlock
              errors={
                fieldError ||
                formatMessage({ id: otpErrorData.errorText }) ||
                formatMessage({ id: 'otp.dialog.error.tooManyRequests' })
              }
              className={styles.errorBlock}
            />
          </If>

          <Button
            className={styles.getPasscodeBtn}
            color="primary"
            raised
            type="button"
            onClick={handleSubmitPasscode}
            disabled={Boolean(fieldError || !fieldValue || otpErrorData.error)}
          >
            {formatMessage({ id: 'otp.dialog.getPasscode' })}
          </Button>

          <Button
            className={cn(styles.cancelBtn, 'db w-100 lh-copy')}
            color="accent"
            type="button"
            onClick={() => {
              handleCancelBtnClick();
              setFieldValue('');
            }}
          >
            {formatMessage({ id: 'common.cancel' })}
          </Button>
        </div>
      </Popup>

      {isOtpConfirmationOpened && (
        <MfaDialog
          counterValue={otpCodeExpirationSeconds}
          disableVerifyButton={loginRejected}
          error={otpErrorData.error}
          errorText={otpErrorData.errorText}
          loading={sending}
          onClose={handleCloseOtpDialog}
          onResend={handleResendOtpCode}
          onSubmit={handleOtpLogin}
          open={isOtpConfirmationOpened}
          title={formatMessage({ id: 'otp.dialog.title' })}
          withContentText={formatMessage({
            id: 'mfa.dialog.phoneAndEmailConfirmation',
          })}
        />
      )}

      <InvitationModal
        formatMessage={formatMessage}
        opened={invitationModalData.showInvitationModal}
        loginValue={invitationModalData.loginValue}
        handleCloseInvitationModal={handleCloseInvitationModal}
      />
    </div>
  );
};

OtpModal.propTypes = {
  open: PropTypes.bool,
  formatMessage: PropTypes.func,
  onClose: PropTypes.func,
  setIsAnyOfOtpPathModalsOpen: PropTypes.func,
};

OtpModal.defaultProps = {
  open: false,
  formatMessage: () => {},
  onClose: () => {},
};

export default OtpModal;



// WEBPACK FOOTER //
// ./src/components/OtpModal/index.jsx